(() => {
  if (customElements.get('croma-cart-drawer')) {
    return;
  }

  class CromaCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.croma-cart-drawer__panel');
      this.titleTarget = this.querySelector('#CromaCartDrawerTitle');
      this.body = this.querySelector('[data-cart-drawer-body]');
      this.itemsContainer = this.querySelector('[data-cart-drawer-items]');
      this.emptyState = this.querySelector('[data-cart-drawer-empty]');
      this.footer = this.querySelector('[data-cart-drawer-footer]');
      this.countTargets = this.querySelectorAll('[data-cart-drawer-count]');
      this.subtotalTarget = this.querySelector('[data-cart-drawer-subtotal]');
      this.noteToggle = this.querySelector('[data-cart-note-toggle]');
      this.notePanel = this.querySelector('[data-cart-note-panel]');
      this.noteInput = this.querySelector('[data-cart-note-input]');
      this.noteStatus = this.querySelector('[data-cart-note-status]');
      this.statusTarget = this.querySelector('[data-cart-drawer-status]');
      this.locale = document.documentElement.lang || 'en';
      this.isOpen = false;
      this.isUpdating = false;
      this.noteSaveTimeout = null;
      this.noteDirty = false;
      this.inertStates = [];

      this.addEventListener('click', this.onDrawerClick);
      this.noteInput?.addEventListener('input', this.onNoteInput);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onDrawerClick);
      this.noteInput?.removeEventListener('input', this.onNoteInput);
      window.clearTimeout(this.noteSaveTimeout);
      this.setPageInert(false);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onDocumentClick = (event) => {
      const trigger = event.target.closest('[data-croma-cart-toggle]');

      if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      event.preventDefault();
      this.open(trigger);
    };

    onDrawerClick = (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close();
        return;
      }

      const noteToggle = event.target.closest('[data-cart-note-toggle]');

      if (noteToggle) {
        this.toggleNotePanel();
        return;
      }

      const quantityButton = event.target.closest('[data-cart-drawer-quantity]');

      if (quantityButton) {
        this.changeLine(quantityButton.dataset.line, quantityButton.dataset.quantity);
        return;
      }

      const removeButton = event.target.closest('[data-cart-drawer-remove]');

      if (removeButton) {
        this.changeLine(removeButton.dataset.line, 0);
      }
    };

    onNoteInput = () => {
      this.noteDirty = true;
      const checkoutNote = this.querySelector('[data-cart-checkout-note]');
      if (checkoutNote) checkoutNote.value = this.noteInput.value;
      window.clearTimeout(this.noteSaveTimeout);

      if (this.noteStatus) {
        this.noteStatus.hidden = true;
      }

      this.noteSaveTimeout = window.setTimeout(() => {
        this.updateNote(this.noteInput.value);
      }, 500);
    };

    onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
        return;
      }

      if (event.key === 'Tab' && this.isOpen) {
        this.trapFocus(event);
      }
    };

    async open(trigger = this.lastTrigger) {
      if (trigger) {
        this.lastTrigger = trigger;
      }

      this.isOpen = true;
      this.hidden = false;
      this.setPageInert(true);
      document.body.classList.add('croma-cart-drawer-open');

      window.requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.updateTriggerExpanded(true);
        (this.titleTarget || this.panel)?.focus({ preventScroll: true });
      });

      await this.refresh();
      if (this.body) {
        this.body.scrollTop = 0;
      }
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.updateTriggerExpanded(false);
      this.setPageInert(false);
      document.body.classList.remove('croma-cart-drawer-open');

      window.setTimeout(() => {
        if (!this.isOpen) {
          this.hidden = true;
        }
      }, 260);

      this.lastTrigger?.focus({ preventScroll: true });
    }

    async refresh() {
      try {
        const cart = await this.fetchCart();
        this.render(cart);
      } catch (error) {
        console.error('Unable to refresh cart drawer', error);
        this.announceError();
      }
    }

    async fetchCart() {
      return window.CromaCart.run(request => request('cart.js'));
    }

    async changeLine(line, quantity) {
      if (this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      this.classList.add('is-updating');
      this.clearStatus();

      try {
        await window.CromaCart.run(async request => {
          const page = document.querySelector('croma-cart-discounts[data-section-id]');
          const sectionId = page?.dataset.sectionId;
          const cart = await request('cart/change.js', {
            line: Number(line),
            quantity: Math.max(Number(quantity), 0),
            ...(sectionId ? { sections: [sectionId], sections_url: `${this.routesRoot}cart` } : {})
          });
          this.render(cart);
          if (sectionId) await page.refreshPage(cart, sectionId);
        });
      } catch (error) {
        console.error('Unable to update cart drawer', error);
        this.announceError();
      } finally {
        this.isUpdating = false;
        this.classList.remove('is-updating');
      }
    }

    render(cart) {
      const itemCount = Number(cart.item_count || 0);

      this.countTargets.forEach((target) => {
        target.textContent = itemCount;
      });

      if (this.subtotalTarget) {
        this.subtotalTarget.textContent = this.formatMoney(cart.total_price, cart.currency);
      }

      this.emptyState.hidden = itemCount > 0;
      this.footer.hidden = itemCount === 0;
      this.updateNoteInput(cart.note || '');
      this.itemsContainer.innerHTML = cart.items.map((item, index) => this.renderItem(item, index + 1, cart.currency)).join('');
      this.updateHeaderCounts(itemCount);
      this.querySelectorAll('croma-cart-discounts').forEach(element => element.renderCodes(cart));
      const discounts = this.querySelector('[data-cart-drawer-discounts]');
      if (discounts) {
        discounts.innerHTML = (cart.cart_level_discount_applications || []).map(discount =>
          `<p class="croma-cart-discounts__status">${this.escapeHTML(discount.title)} (-${this.formatMoney(discount.total_allocated_amount, cart.currency)})</p>`
        ).join('');
      }
    }

    toggleNotePanel() {
      if (!this.noteToggle || !this.notePanel) {
        return;
      }

      const isExpanded = this.noteToggle.getAttribute('aria-expanded') === 'true';
      const toggleIcon = this.noteToggle.querySelector('.croma-cart-drawer__note-toggle-icon');

      this.noteToggle.setAttribute('aria-expanded', String(!isExpanded));
      this.notePanel.hidden = isExpanded;

      if (toggleIcon) {
        toggleIcon.textContent = isExpanded ? '+' : '-';
      }

      if (!isExpanded) {
        this.noteInput?.focus({ preventScroll: true });
      }
    }

    async updateNote(note) {
      this.clearStatus();

      try {
        const cart = await window.CromaCart.run(request => request('cart/update.js', { note }));

        if (this.noteInput?.value === note && (cart.note || '') === note) this.noteDirty = false;
        this.updateNoteInput(cart.note || '');

        if (!this.noteDirty && this.noteStatus && this.dataset.noteSavedLabel) {
          this.noteStatus.textContent = this.dataset.noteSavedLabel;
          this.noteStatus.hidden = false;
        }
      } catch (error) {
        console.error('Unable to update cart note', error);
        this.announceError();
      }
    }

    trapFocus(event) {
      if (!this.panel) {
        return;
      }

      const focusableElements = Array.from(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.closest('[hidden]') && element.getClientRects().length > 0);

      if (!focusableElements.length) {
        event.preventDefault();
        this.panel.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (
        event.shiftKey &&
        (activeElement === firstElement || activeElement === this.titleTarget || activeElement === this.panel)
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (activeElement === lastElement || !this.panel.contains(activeElement))) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    setPageInert(isInert) {
      if (isInert) {
        if (this.inertStates.length) {
          return;
        }

        this.inertStates = Array.from(document.body.children)
          .filter((element) => element !== this && element.tagName !== 'SCRIPT')
          .map((element) => ({ element, wasInert: element.inert }));

        this.inertStates.forEach(({ element }) => {
          element.inert = true;
        });
        return;
      }

      this.inertStates.forEach(({ element, wasInert }) => {
        element.inert = wasInert;
      });
      this.inertStates = [];
    }

    clearStatus() {
      if (this.statusTarget) {
        this.statusTarget.textContent = '';
      }
    }

    announceError() {
      if (this.statusTarget) {
        this.statusTarget.textContent = this.dataset.updateErrorLabel || '';
      }
    }

    updateNoteInput(note) {
      if (!this.noteInput || this.noteDirty || document.activeElement === this.noteInput) {
        return;
      }

      this.noteInput.value = note;
      const checkoutNote = this.querySelector('[data-cart-checkout-note]');
      if (checkoutNote) checkoutNote.value = note;
    }

    renderItem(item, line, currency) {
      const imageMarkup = item.image
        ? `<img class="croma-cart-drawer__item-image" src="${this.escapeHTML(item.image)}" alt="${this.escapeHTML(item.title)}" loading="lazy">`
        : '<div class="croma-cart-drawer__item-placeholder" aria-hidden="true"></div>';
      const variantMarkup = item.variant_title
        ? `<p class="croma-cart-drawer__item-variant">${this.escapeHTML(item.variant_title)}</p>`
        : '';
      const title = this.escapeHTML(item.product_title || item.title);
      const quantityLabel = this.escapeHTML(this.dataset.quantityLabel || '');
      const removeLabel = this.escapeHTML(this.dataset.removeLabel || '');
      const decreaseLabel = this.escapeHTML((this.dataset.decreaseTemplate || '').replace('[item]', item.product_title || item.title));
      const increaseLabel = this.escapeHTML((this.dataset.increaseTemplate || '').replace('[item]', item.product_title || item.title));
      const comparePrice = item.original_line_price > item.final_line_price
        ? `<s>${this.formatMoney(item.original_line_price, currency)}</s> ` : '';
      const discounts = (item.line_level_discount_allocations || []).map(discount =>
        `<p class="croma-cart-discounts__status">${this.escapeHTML(discount.discount_application.title)} (-${this.formatMoney(discount.amount, currency)})</p>`
      ).join('');

      return `
        <article class="croma-cart-drawer__item">
          <a class="croma-cart-drawer__item-media" href="${this.escapeHTML(item.url)}">
            ${imageMarkup}
          </a>

          <div class="croma-cart-drawer__item-content">
            <p class="croma-cart-drawer__item-price">${comparePrice}${this.formatMoney(item.final_line_price, currency)}</p>
            ${discounts}
            <a class="croma-cart-drawer__item-title" href="${this.escapeHTML(item.url)}">${title}</a>
            ${variantMarkup}

            <div class="croma-cart-drawer__item-actions">
              <div class="croma-cart-drawer__quantity" aria-label="${quantityLabel}">
                <button class="croma-cart-drawer__quantity-button" type="button" data-cart-drawer-quantity data-line="${line}" data-quantity="${item.quantity - 1}" aria-label="${decreaseLabel}">-</button>
                <span class="croma-cart-drawer__quantity-value">${item.quantity}</span>
                <button class="croma-cart-drawer__quantity-button" type="button" data-cart-drawer-quantity data-line="${line}" data-quantity="${item.quantity + 1}" aria-label="${increaseLabel}">+</button>
              </div>

              <button class="croma-cart-drawer__remove" type="button" data-cart-drawer-remove data-line="${line}">${removeLabel}</button>
            </div>
          </div>
        </article>
      `;
    }

    updateHeaderCounts(itemCount) {
      const label = this.dataset.cartCountTemplate.replace('[count]', itemCount);
      const textLabel = (this.dataset.cartTextTemplate || '').replace('[count]', itemCount);

      document.querySelectorAll('[data-croma-cart-toggle]').forEach((trigger) => {
        trigger.setAttribute('aria-label', label);

        const badge = trigger.querySelector('.croma-header__cart-count');
        const text = trigger.querySelector('.croma-header__cart-text');

        if (badge) {
          badge.textContent = itemCount;
        }

        if (text) {
          text.textContent = textLabel;
        }
      });
    }

    updateTriggerExpanded(isExpanded) {
      document.querySelectorAll('[data-croma-cart-toggle]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(isExpanded));
      });
    }

    formatMoney(cents, currency = 'EUR') {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
      }
    }

    escapeHTML(value) {
      const element = document.createElement('span');
      element.textContent = value ?? '';
      return element.innerHTML;
    }

    get routesRoot() {
      return window.Shopify?.routes?.root || '/';
    }
  }

  customElements.define('croma-cart-drawer', CromaCartDrawer);
})();
